import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Status pill. Tones name the visual weight, not a domain vocabulary — section
 * completion, prerequisite status and assessment lifecycle each map their own
 * states onto these, so the pill geometry stays in one place while the wording
 * stays with the feature that owns it.
 *
 * Pale tints carry a neutral text role, never the state colour or a darker shade of
 * it — state hues are for solid fills (with their `-on` foreground), icons and borders.
 */
const badgeVariants = cva(
  'inline-block whitespace-nowrap rounded-input font-semibold uppercase tracking-[0.08em]',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-hover text-muted',
        progress: 'bg-progress-bg text-body',
        success: 'bg-success-bg text-body',
        open: 'bg-status-open text-status-open-on',
        active: 'bg-status-in-progress text-status-in-progress-on',
        submitted: 'bg-status-submitted text-status-submitted-on',
        danger: 'bg-danger text-danger-on',
      },
      size: {
        sm: 'px-2.5 py-1 text-[11px]',
        md: 'px-3 py-1.5 text-[12px] font-bold',
      },
    },
    defaultVariants: {
      tone: 'neutral',
      size: 'sm',
    },
  },
)

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>

export interface BadgeProps extends VariantProps<typeof badgeVariants> {
  children: React.ReactNode
  className?: string
}

export function Badge({ tone, size, className, children }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone, size }), className)}>{children}</span>
}

export { badgeVariants }
